import { useState, createContext, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import Postmonger from "vis-postmonger";
import axios from "axios";
import { fieldNames } from "../components/common/constants";

const PostmongerContext = createContext();

const PostmongerProvider = (props) => {
  const [state, setState] = useState({
    inArgs: new Map(),
    outArgs: new Map(),
    culture: "en-US",
    token: "",
    seedData: {},
    interaction: {},
    dataExtensionId: "",
  });

  const { i18n, t } = useTranslation();
  const APIUrl = `${window.location.origin}/api/1.0`;
  const isLocalhost =
    location.hostname === "localhost" || location.hostname === "127.0.0.1";

  const [tokens, setTokens] = useState(null);
  const [endPoints, setEndPoints] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchingSeed, setFetchingSeed] = useState(false);
  const [fetchingToken, setFetchingToken] = useState(false);
  const [interaction, setInteraction] = useState(state.interaction);
  const [currentMID, setCurrentMID] = useState(isLocalhost ? 7313840 : -1); // Dev01 to be able to debug locally
  const [dataExtensionId, setDataExtensionId] = useState(state.dataExtensionId);
  const [userInfo, setUserInfo] = useState(
    isLocalhost
      ? {
          locale: { localeCode: process.env.REACT_APP_LOCALE_CODE ?? "en-NZ" },
          timeZone: { name: "(GMT+12:00) Auckland, Wellington" },
        }
      : null
  ); // default locale and timezone for local debug. Locale can be changed by setting the REACT_APP_LOCALE_CODE enviroment variable.
  const [disabled, setDisabled] = useState(true);
  const [noTopicAvailable, setNoTopicAvailable] = useState(true);
  const [activity, setActivity] = useState(
    isLocalhost
      ? {
          arguments: { execute: {} },
          metaData: {},
          Id: "fake",
        }
      : null
  );

  if (isLocalhost && (!interaction || !interaction.id)) {
    state.interaction = {
      id: "12345678-9abc-def0-1234-56789abcdef0",
      status: "DRAFT",
    }; // Some dummy Guid to be able to debug locall       setState(state);
    setInteraction(state.interaction);
  }

  const connection = new Postmonger.Session();

  const readInArguments = (inArguments) => {
    state.inArgs = new Map();
    inArguments.forEach((value) =>
      state.inArgs.set(Object.keys(value)[0], value[Object.keys(value)[0]])
    );
    setState(state);
    console.log("readInArguments", state.inArgs);
  };

  const writeInArguments = (inArguments) => {
    state.inArgs.forEach((value, key) => {
      const o = {};
      o[key] = value;
      inArguments.push(o);
    });
  };

  const setupExampleTestHarness = () => {
    if (!isLocalhost) {
      // don't load the test harness functions when running in Journey Builder
      return;
    }

    const jbSession = new Postmonger.Session();
    const jb = {};
    window.jb = jb;

    jbSession.on("setActivityDirtyState", function (value) {
      console.log("[echo] setActivityDirtyState -> ", value);
    });

    jbSession.on("requestInspectorClose", function () {
      console.log("[echo] requestInspectorClose");
    });

    jbSession.on("updateActivity", function (activity) {
      console.log(
        "[echo] updateActivity -> ",
        JSON.stringify(activity, null, 4)
      );
    });

    jbSession.on("ready", function () {
      console.log("[echo] ready");
      console.log(
        "\tuse jb.ready() from the console to initialize your activity"
      );
    });

    // fire the ready signal with an example activity
    jb.ready = function () {
      jbSession.trigger("initActivity", {
        name: "",
        key: "EXAMPLE-1",
        metaData: {},
        configurationArguments: {},
        arguments: {
          executionMode: "{{Context.ExecutionMode}}",
          definitionId: "{{Context.DefinitionId}}",
          activityId: "{{Activity.Id}}",
          contactKey: "{{Context.ContactKey}}",
          execute: {
            inArguments: [
              {
                description: "Testing",
              },
              {
                rootBuId: 7313705,
              },
              {
                nodeType: "Webhook",
              },
              {
                currentBuId: 7313879,
              },
              {
                "webhook.Profile": "inactive-webhook-profile",
              },
              {
                "webhook.Name": 'a "no good" Profile',
              },
              {
                "webhook.EndpointURL": "https://iqvia.com/",
              },
              {
                "webhook.Payload":
                  '[{"keys": {"Contact Key": "$(Contact Key)"}, "values": {"First Name": "$(First Name)", "Last Name": "$(Last Name)", "Updated Date": "$(NOW)"}}]',
              },
            ],
            outArguments: [],
          },
          startActivityKey: "{{Context.StartActivityKey}}",
          definitionInstanceId: "{{Context.DefinitionInstanceId}}",
          requestObjectId: "{{Context.RequestObjectId}}",
        },
      });
    };
  };

  useEffect(() => {
    if (activity) {
      const hasInArguments = Boolean(
        activity.arguments &&
          activity.arguments.execute &&
          activity.arguments.execute.inArguments &&
          activity.arguments.execute.inArguments.length > 0
      );

      const inArguments = hasInArguments
        ? activity.arguments.execute.inArguments
        : [];

      readInArguments(inArguments);
      state.inArgs.set("description", activity.name ?? t("generic.title"));

      console.log("activity:\n ", JSON.stringify(activity, null, 4));
      console.log("Has In Arguments: ", hasInArguments);
      console.log("inArguments", inArguments);
    }
  }, [activity]);

  const onInitActivity = (payload) => {
    console.log("initActivity:\n ", JSON.stringify(payload, null, 4));
    connection.trigger("requestInteraction");
    connection.trigger("requestTriggerEventDefinition");
    connection.trigger("requestTokens");
    connection.trigger("requestEndpoints");
    setActivity(payload);
  };

  const onRequestedInteraction = (interaction) => {
    console.log("interaction:\n ", JSON.stringify(interaction, null, 4));
    state.interaction = interaction;
    setState(state);
    setInteraction(interaction);
  };

  const onRequestedTriggerEventDefinition = (eventDefinitionModel) => {
    console.log(
      "eventDefinitionModel:\n ",
      JSON.stringify(eventDefinitionModel, null, 4)
    );
    state.dataExtensionId = eventDefinitionModel.dataExtensionId;
    setState(state);
    setDataExtensionId(eventDefinitionModel.dataExtensionId);

    var deAudience = `${eventDefinitionModel.eventDefinitionKey}`;
    var firstNameLookup = `Event.${deAudience}.\"Contact First Name\"`;
    var lastNameLookup = `Event.${deAudience}.\"Contact Last Name\"`;

    state.inArgs.set("emailAddress", "{{InteractionDefaults.Email}}");
    state.inArgs.set("firstName", `{{${firstNameLookup}}}`);
    state.inArgs.set("lastName", `{{${lastNameLookup}}}`);
  };

  const onRequestedTokens = (inputTokens) => {
    console.log("inputTokens:\n ", JSON.stringify(inputTokens, null, 4));
    setTokens(inputTokens);
  };

  const onRequestedEndpoints = (endpoints) => {
    console.log("endpoints:\n ", JSON.stringify(endpoints, null, 4));
    setEndPoints(endpoints);
  };

  const onRequestCulture = (cultureCodeString) => {
    console.log("culture: ", cultureCodeString);
    state.culture = cultureCodeString;
    setState(state);
  };

  useEffect(() => {
    // Setup a test harness so we can interact with our custom activity
    // outside of journey builder using window functions & browser devtools.
    // This isn't required by your activity, its for example purposes only
    setupExampleTestHarness();

    // Bind the initActivity event...
    // Journey Builder will respond with "initActivity" after it receives the "ready" signal
    connection.on("initActivity", onInitActivity);
    connection.on("requestedInteraction", onRequestedInteraction);
    connection.on(
      "requestedTriggerEventDefinition",
      onRequestedTriggerEventDefinition
    );
    connection.on("requestedTokens", onRequestedTokens);
    connection.on("requestedEndpoints", onRequestedEndpoints);
    connection.on("requestedCulture", onRequestCulture);

    // We're all set! let's signal Journey Builder
    // that we're ready to receive the activity payload...

    // Tell the parent iFrame that we are ready.
    connection.trigger("ready");
  }, []);

  const fetchTokenData = async (rootMID, journeyId) => {
    try {
      setFetchingToken(true);
      const url = `${APIUrl}/SeedData/token/${rootMID}/${tokens.fuel2token}/${journeyId}`;
      const response = await axios.get(url);
      if (response.status === 200) {
        state.inArgs.set("currentBuId", response.data.organization.id);
        setState(state);
        setCurrentMID(response.data.organization.id);
        setUserInfo(response.data.userDetails);
        i18n.changeLanguage(response.data.userDetails.locale.localeCode);
      }
    } catch (error) {
      console.log("error checking token", error);
    } finally {
      setFetchingToken(false);
    }
  };

  useEffect(() => {
    if (interaction) {
      let journeyId = interaction.id;
      if (journeyId === null) {
        journeyId = "key:" + interaction.key;
      }
      if (tokens && state.inArgs.get("rootBuId") > 0 && journeyId) {
        fetchTokenData(state.inArgs.get("rootBuId"), journeyId);
      }
    }
  }, [tokens, interaction]);

  useEffect(() => {
    if (interaction && interaction.id && interaction.id != null) {
      setDisabled(interaction.status !== "DRAFT");
    }
  }, [interaction]);

  const fetchSeedData = async (rootMID, journeyId, userInfo) => {
    try {
      setFetchingSeed(true);
      const timeZone =
        userInfo.timeZone && userInfo.timeZone.name
          ? userInfo.timeZone.name
          : "none";
      const url = `${APIUrl}/SeedData/data/${rootMID}/${journeyId}/${userInfo.locale.localeCode}/${timeZone}`;
      const response = await axios.get(url);
      if (response.status === 200) {
        state.seedData = response.data.seedData;
        console.log("SeedData: ", state.seedData);
        setState(state);
        console.log("TopicName: ", response.data.seedData.Metadata.TopicName);
        if (
          response.data.seedData &&
          response.data.seedData.Metadata &&
          response.data.seedData.Metadata.TopicName
        ) {
          setNoTopicAvailable(false);
        } else {
          setDisabled(true);
          if (isLocalhost) {
            // for debugging purpoose
            setNoTopicAvailable(false);
            setDisabled(false);
          }
        }
      }
    } catch (error) {
      console.log("error getting seed data", error);
    } finally {
      setFetchingSeed(false);
      setIsLoading(false);
    }
  };


  useEffect(async () => {
    console.log("getSeedData", state.inArgs);
    if (interaction && userInfo) {
      var journeyId = interaction.id;
      if (journeyId === null && interaction.key) {
        journeyId = "key:" + interaction.key;
      }
      console.log(
        "currentMID: ",
        currentMID,
        " journeyId: ",
        journeyId,
        " id: ",
        interaction.id,
        " key: ",
        interaction.key
      );
      if (currentMID > 0 && journeyId) {
        await fetchSeedData(currentMID, journeyId, userInfo);
        await getFeatureFlags();
        await getIsHCOFeatureEnabled(currentMID);
      } else {
        setDisabled(true);
      }
      const isHco = state.seedData && state.seedData.Metadata && state.seedData.Metadata.IsHCO === true && state.seedData.isHCOEnabled === true ? true : false;
      if (isHco) {
        state.inArgs.set("nodeType", "FTPFileUpload");
      }
      state.inArgs.set("isHco", isHco);
      console.log("getSeedData-final", state.inArgs);
      if (state.inArgs.get(fieldNames.webinarConnectionId))
        await getWebinarConnections();
      if (state.inArgs.get(fieldNames.webinarId))
        await getWebinars(state.inArgs.get(fieldNames.webinarConnectionId));
      if (state.inArgs.get(fieldNames.occurrenceId))
        await getWebinarOccurrences(
          state.inArgs.get(fieldNames.webinarConnectionId),
          state.inArgs.get(fieldNames.webinarId)
        );
    }
  }, [interaction, currentMID, userInfo]);

  const signalDone = () => {
    // we set must metaData.isConfigured in order to tell JB that
    // this activity is ready for activation
    console.log("-------- triggered:signalDone({obj}) ------------");
    console.log("activity:\n ", JSON.stringify(activity, null, 4));
    console.log("-------------------------------------------------");

    activity.metaData.isConfigured = false;

    activity.arguments.execute.inArguments = [];

    writeInArguments(activity.arguments.execute.inArguments);

    // you can set the name that appears below the activity with the name property
    if (!noTopicAvailable) {
      activity.name = state.inArgs.get("nodeType");
      activity.metaData.isConfigured = true;
    }
    const description = state.inArgs.get("description");

    if (description !== "") {
      activity.name = description;
    }

    console.log(
      "------------ triggering:updateActivity({obj}) ----------------"
    );
    console.log("Sending message back to updateActivity");
    console.log("saving\n", JSON.stringify(activity, null, 4));
    console.log(
      "--------------------------------------------------------------"
    );

    connection.trigger("updateActivity", activity);
  };

  const signalCancelled = () => {
    if (noTopicAvailable) {
      signalDone();
      return;
    }

    connection.trigger("setActivityDirtyState", false);

    // now request that Journey Builder closes the inspector/drawer
    connection.trigger("requestInspectorClose");
  };

  const setDirty = () => {
    connection.trigger("setActivityDirtyState", true);
    setState(state);
  };

  const getFeatureFlags = async () => {
    try {
      setIsLoading(true);
      let rootBuId = state.inArgs.get("rootBuId");
      let childBuId = state.inArgs.get("currentBuId");
      const url = `${APIUrl}/MeetEasy/GetFeatureFlags/${childBuId}/${rootBuId}`;
      const response = await axios.get(url);
      if (response.status === 200) {
        state.seedData.featureFlags = response.data;
        setState(state);
      }
    } catch (error) {
      console.log("error getting feature flags", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getIsHCOFeatureEnabled = async (currentBuId) => {
    try {
      setIsLoading(true);
      const url = `${APIUrl}/SeedData/isHCOFeatureEnabled/${currentBuId}`;
      const response = await axios.get(url);
      if (response.status === 200) {
        state.seedData.isHCOEnabled = response.data;
        setState({...state});
      }
    } catch (error) {
      console.log("error getting HCO Feature Flag ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getWebinarConnections = async () => {
    try {
      setIsLoading(true);
      let rootBuId = state.inArgs.get("rootBuId");
      let childBuId = state.inArgs.get("currentBuId");
      const url = `${APIUrl}/MeetEasy/GetWebinarConnections/${childBuId}/${rootBuId}`;
      const response = await axios.get(url);
      if (response.status === 200) {
        state.seedData.Connections = response.data;
        setState(state);
      }
    } catch (error) {
      console.log("error getting webinar connections", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getWebinars = async (webinarConnectionId) => {
    try {
      setIsLoading(true);
      let rootBuId = state.inArgs.get("rootBuId");
      let childBuId = state.inArgs.get("currentBuId");
      const url = `${APIUrl}/MeetEasy/GetWebinars/${childBuId}/${rootBuId}/${webinarConnectionId}`;
      const response = await axios.get(url);
      if (response.status === 200) {
        state.seedData.Webinars = response.data;
        setState(state);
      }
    } catch (error) {
      console.log("error getting webinars", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getWebinarOccurrences = async (webinarConnectionId, webinarId) => {
    try {
      setIsLoading(true);
      let rootBuId = state.inArgs.get("rootBuId");
      let childBuId = state.inArgs.get("currentBuId");
      const url = `${APIUrl}/MeetEasy/GetWebinarOccurrences/${childBuId}/${rootBuId}/${webinarConnectionId}/${webinarId}`;
      const response = await axios.get(url);
      if (response.status === 200) {
        state.seedData.Occurrences = response.data;
        setState(state);
      }
    } catch (error) {
      console.log("error getting webinar occurrences", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PostmongerContext.Provider
      value={{
        isLocalhost: isLocalhost,
        isLoading: isLoading || fetchingSeed || fetchingToken,
        inArguments: state.inArgs,
        outArguments: state.outArgs,
        culture: state.culture,
        token: state.token,
        seedData: state.seedData,
        interaction: state.interaction,
        userInfo: userInfo,
        disabled: disabled,
        noTopicAvailable: noTopicAvailable,
        dataExtensionId: dataExtensionId,
        signalDone: () => signalDone(),
        signalCancelled: () => signalCancelled(),
        setDirty: () => setDirty(),
        getWebinarConnections: () => getWebinarConnections(),
        getWebinars: (webinarConnectionId) => getWebinars(webinarConnectionId),
        getWebinarOccurrences: (webinarConnectionId, webinarId) =>
          getWebinarOccurrences(webinarConnectionId, webinarId),
      }}
    >
      {props.children}
    </PostmongerContext.Provider>
  );
};

const usePostmongerContext = () => useContext(PostmongerContext);

export { PostmongerContext, PostmongerProvider, usePostmongerContext };
